/*
 * lb-upsell.js patches window.fetch on this store with a broken wrapper
 * that silently swallows /cart/add.js requests. Streamline's native
 * add-to-cart flow (theme.AjaxProduct) uses fetch() for that request,
 * which means the native Add to Cart button on every product page is
 * affected: the request hangs/delays, and because the response promise
 * never resolves cleanly, success() never runs, so the ajaxProduct:added
 * event never fires and the cart drawer never opens automatically.
 *
 * This file replaces just that one method with an XMLHttpRequest-based
 * version, which lb-upsell.js does not touch. Everything else about the
 * flow - loading state, success/error handling, the ajaxProduct:added
 * event, the cart-page reload - stays identical to the original.
 *
 * Must load, deferred, immediately after theme.min.js and before
 * DOMContentLoaded fires, so this override is in place before any
 * `new theme.AjaxProduct(...)` instance binds its submit handler.
 */
(function () {
  'use strict';

  if (!window.theme || !theme.AjaxProduct || !theme.AjaxProduct.prototype) return;

  var loading = false;

  theme.AjaxProduct.prototype.addItemFromForm = function (evt) {
    evt.preventDefault();
    if (loading) return;

    var self = this;
    var addToCart = this.addToCart;
    var data = theme.utils.serialize(this.form);

    if (addToCart) addToCart.classList.add('btn--loading');
    loading = true;

    var xhr = new XMLHttpRequest();
    xhr.open('POST', theme.routes.cartAdd, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    xhr.onload = function () {
      loading = false;
      if (addToCart) addToCart.classList.remove('btn--loading');

      var response;
      try {
        response = JSON.parse(xhr.responseText);
      } catch (e) {
        console.warn('Add to cart: could not parse response', e);
        return;
      }

      if (response.status === 422) {
        self.error(response);
      } else {
        self.success(response);
      }

      if (document.body.classList.contains('template-cart')) {
        window.scrollTo(0, 0);
        location.reload();
      }
    };

    xhr.onerror = function () {
      loading = false;
      if (addToCart) addToCart.classList.remove('btn--loading');
      console.warn('Add to cart request failed');
    };

    xhr.send(data);
  };

  /*
   * The theme already has a single shared function that runs after every
   * cart mutation - add, remove, quantity change, all of it - called
   * theme.CartForm.prototype.updateCount. It updates the stock badge
   * class .cart-link__bubble, which our custom header doesn't use (ours
   * is .ornavo-header__cart-count), so every one of those updates was
   * silently finding nothing to update. Wrapping this one method - rather
   * than chasing each individual event (add, remove, qty change)
   * separately - covers all of them at once, since they all funnel
   * through here already.
   */
  if (window.theme && theme.CartForm && theme.CartForm.prototype && theme.CartForm.prototype.updateCount) {
    var originalUpdateCount = theme.CartForm.prototype.updateCount;
    theme.CartForm.prototype.updateCount = function (count) {
      originalUpdateCount.call(this, count);

      var badge = document.querySelector('.ornavo-header__cart-count');
      if (badge) {
        badge.textContent = count;
        badge.classList.toggle('is-empty', count === 0);
      }
    };
  }

  /*
   * The two patches above only cover the theme's own native add-to-cart
   * flow. Third-party apps - Frequently Bought Together bundles, upsell
   * widgets, quick-buy buttons, anything not part of the theme - have
   * their own separate scripts, and some of them call fetch() directly
   * for /cart/add.js, /cart/update.js etc. Those hit the exact same
   * lb-upsell.js problem: request goes out, but the app's own button
   * never finds out it succeeded, so it spins forever even though the
   * item was actually added.
   *
   * We can't edit those apps' source directly. Instead, this wraps
   * window.fetch itself: any request aimed at a cart endpoint gets
   * quietly rerouted through XMLHttpRequest instead (which lb-upsell.js
   * doesn't touch), while everything else keeps going through fetch
   * completely unchanged. This fixes Frequently Bought Together without
   * knowing anything about how it's built internally, and will do the
   * same automatically for any other app that hits this same wall later.
   */
  if (typeof window.fetch === 'function') {
    var CART_ENDPOINT_PATTERN = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)|\/cart\.js(\?|$)/;
    var previousFetch = window.fetch;

    function xhrFetch(url, options) {
      options = options || {};
      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open(options.method || 'GET', url, true);

        if (options.headers) {
          if (typeof options.headers.forEach === 'function') {
            options.headers.forEach(function (value, key) { xhr.setRequestHeader(key, value); });
          } else {
            Object.keys(options.headers).forEach(function (key) {
              xhr.setRequestHeader(key, options.headers[key]);
            });
          }
        }

        xhr.onload = function () {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            statusText: xhr.statusText,
            headers: { get: function () { return xhr.getResponseHeader('Content-Type'); } },
            json: function () { return Promise.resolve().then(function () { return JSON.parse(xhr.responseText); }); },
            text: function () { return Promise.resolve(xhr.responseText); }
          });
        };

        xhr.onerror = function () { reject(new TypeError('Network request failed')); };

        xhr.send(options.body || null);
      });
    }

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (CART_ENDPOINT_PATTERN.test(url)) {
        return xhrFetch(url, init);
      }
      return previousFetch.apply(this, arguments);
    };
  }
})();